import React, {
  FormEvent,
  FormEventHandler,
  useContext,
  useState,
} from 'react';
import { startJobWithZipAndPayload } from '../services/job-api';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { NavigateToPageRoute } from '../utils/common-functions';
import { useNavigate } from 'react-router-dom';
import { AddJobPayload } from '../models/job/AddJobPayload';
export const useAddNewJobWithZip = (
  token: string,
  setError: React.Dispatch<React.SetStateAction<string>>,
  fileType: string,
  invoiceType: string,
  networkProvider: string,
  serviceProvider: string,
  companyName: string,
  companyId: string,
  userId: string,
  tariff: string,
  ratingAccountingType: string,
  simOnlyThreshold: string,
  maxRatingPercentage: string,
  baseFeeDiscountRate: string,
  basicFeeServiceProvider: string,
  omitSIMOnly: boolean,
  omitSIMOnlyForSmartphones: boolean,
  addVAT: boolean,
  omitPersistRecords: boolean,
  businessAccount: boolean,
  enterpriseDataUsage: boolean,
  omitContractWithoutCallDetails: boolean,
  withOptions: boolean,
  recoveryMode: boolean,
  rateRateBigFour: boolean,
  rateOwnProvider: boolean,
  includeVPN: boolean,
  serviceProviders: string,
  targetGroups: string,
  connectionCostDiscountRates: string,
  appleNumbers: string,
  selectedPhones: string,
) => {
  const [queryInProgress, setQueryInProgress] = useState<boolean>(false);
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [fileName, setFileName] = React.useState<string>('');

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (
    e: FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault(); // avoid double submission
    if (fileBlob) {
      console.debug('Starting job with zip and payload...');
      await addNewJobWithZip();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setQueryInProgress(true);
    const file = event.target.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.zip')) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const blob = new Blob([arrayBuffer]);
        setFileBlob(blob);
        console.debug('Blob created:', blob);
        setQueryInProgress(false);
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError('Invalid file type. Please select a ZIP file.');
      setQueryInProgress(false);
    }
  };

  const addNewJobWithZip = async () => {
    setQueryInProgress(true);

    const payload: AddJobPayload = {
      token: token,
      fileType: fileType,
      invoiceType: invoiceType,
      networkProvider: networkProvider,
      serviceProvider: serviceProvider,
      clientName: companyName,
      customerCompanyId: parseInt(companyId),
      userId: userId,
      tariffFlag: tariff,
      simOnlyThreshold: simOnlyThreshold,
      maxRatingPercentage: maxRatingPercentage,
      baseFeeDiscountRate: baseFeeDiscountRate,
      basicFeeServiceProvider: basicFeeServiceProvider,
      analysisType: ratingAccountingType,
      omitSIMOnly: omitSIMOnly,
      omitSIMOnlyForSmartphones: omitSIMOnlyForSmartphones,
      addVAT: addVAT,
      omitPersistRecords: omitPersistRecords,
      businessAccount: businessAccount,
      enterpriseDataUsage: enterpriseDataUsage,
      omitContractWithoutCallDetails: omitContractWithoutCallDetails,
      withOptions: withOptions,
      recoveryMode: recoveryMode,
      rateRateBigFour: rateRateBigFour,
      rateOwnProvider: rateOwnProvider,
      includeVPN: includeVPN,
      selectedServiceProviders: serviceProviders,
      targetGroups: targetGroups,
      connectionCostDiscountRates: connectionCostDiscountRates,
      appleNumbers: appleNumbers,
      selectedPhones: selectedPhones,
    };

    console.debug('Payload:', payload);

    const formData = new FormData();
    formData.append(
      'payload',
      new Blob([JSON.stringify(payload)], { type: 'application/json' }),
    );
    // take the fileName without the full path:
    const myFileName = fileName.split('\\').pop();
    formData.append('file', fileBlob as Blob, myFileName);

    const response = await startJobWithZipAndPayload(formData);

    setQueryInProgress(false);

    if (response.success) {
      const message = 'Job added successfully.';
      NavigateToPageRoute(
        `/rating-jobs?showMessage=${message}&messageType=success`,
        setCurrentRoute,
        navigate,
      );
    } else {
      setError(response.userMessage);
    }
  };

  return {
    handleFileChange,
    handleSubmit,
    queryInProgress,
    setQueryInProgress,
    fileName,
    setFileName,
  };
};
